import type {
  ContinueOptions,
  ScreenMembersOnMfaEmailChallenge,
} from '@auth0/auth0-acul-react/types';
import {
  continueMethod,
  resendCode,
  tryAnotherMethod,
  useMfaEmailChallenge,
  useResend,
  useScreen,
  useUser,
} from '@auth0/auth0-acul-react/mfa-email-challenge';

import { MFA_REDIRECT_COOKIE } from '@/constants/mfa';
import { setCookie } from '@/utils/helpers/cookie';
import { executeSafely } from '@/utils/helpers/executeSafely';

export const useMfaEmailChallengeManager = () => {
  const { texts, data } = useScreen();

  const showRememberDevice = data?.showRememberDevice ?? false;

  const handleResendEmail = async (): Promise<void> => {
    await executeSafely(`Resend email with code`, () => resendCode());
  };

  const handleContinue = async (
    code: string,
    rememberDevice: boolean = false,
  ): Promise<void> => {
    const options: ContinueOptions = {
      code: code?.trim() || '',
      rememberDevice,
    };

    await executeSafely(
      `Continue MFA Email Challenge with options: ${JSON.stringify(options)}`,
      () => continueMethod(options),
    );
  };

  // Try another method (select a different authenticator)
  // Sets a cookie to auto-redirect to sms on mfa-login-options
  const handleTryAnotherMethod = async (): Promise<void> => {
    setCookie(MFA_REDIRECT_COOKIE, 'sms');
    await executeSafely(`Try another MFA method`, () => tryAnotherMethod());
  };

  const user = useUser();
  const email =
    user?.userMetadata?.notification_email ??
    user?.enrolledEmails?.[0]?.email ??
    '';

  return {
    mfaEmailChallenge: useMfaEmailChallenge(),
    texts: (texts || {}) as ScreenMembersOnMfaEmailChallenge['texts'],
    handleResendEmail,
    handleContinue,
    handleTryAnotherMethod,
    useResend,
    email,
    showRememberDevice,
  };
};
